import { SlidersHorizontal } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { analyze, applyEscalations } from "../core/analysis";
import type { SimContext } from "../core/context";
import {
  contextForRun,
  isModified,
  settingsForExample,
} from "../core/environments/browser/settings";
import type { SourceSite } from "../core/events";
import { resolveTab } from "../core/timeline";
import { DEFAULT_EXAMPLE, PICKER_EXAMPLES } from "../examples";
import { ConsolePane } from "./ConsolePane";
import { ContextDrawer } from "./ContextDrawer";
import { ExamplePicker } from "./ExamplePicker";
import { EditorPanel, type Reveal } from "./editor/EditorPanel";
import { PagePreview } from "./PagePreview";
import { Pane } from "./Pane";
import { ReportPane } from "./ReportPane";
import { RunButton } from "./RunButton";
import { ThemeToggle } from "./ThemeToggle";
import { TimelinePane } from "./TimelinePane";
import { type EngineStatus, useRunner } from "./useRunner";
import { useTheme } from "./useTheme";

function describeEngine(status: EngineStatus): string {
  if ("state" in status) return "Booting engine…";
  if (status.type === "error") return `Engine failed: ${status.message}`;
  return `Engine ready: ${status.engine.name} ${status.engine.version} (${status.environments.join(", ")})`;
}

export function App() {
  const { engine, timeline, run, stop } = useRunner();
  const { theme, toggle } = useTheme();
  const [example, setExample] = useState(DEFAULT_EXAMPLE);
  const [settings, setSettings] = useState(() => settingsForExample(DEFAULT_EXAMPLE.context));
  const [contextOpen, setContextOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [hover, setHover] = useState<SourceSite | null>(null);
  const [behaviorTab, setBehaviorTab] = useState("timeline");
  const [highlight, setHighlight] = useState<{ seq: number } | null>(null);
  /** What the current run was started with: analysis needs its context and code. */
  const [lastRun, setLastRun] = useState<{ code: string; context: SimContext } | null>(null);
  const code = useRef(example.code);
  // Guards against a selected layer the current run doesn't have.
  const tab = resolveTab(selectedTab, timeline.layers);
  const running = timeline.status === "running";
  const ready = !("state" in engine) && engine.type === "ready";

  const start = useCallback(() => {
    // The simulated date defaults to the real one, frozen at run start.
    if (running) return;
    // The new run's layers replace the old ones, so start back on main.js.
    setSelectedTab(0);
    setReveal(null);
    setHover(null);
    setHighlight(null);
    const context = contextForRun(settings, Date.now());
    setLastRun({ code: code.current, context });
    run(code.current, context, settings.seed);
  }, [running, run, settings]);

  // The report is derived from the store once the run is over (pure, memoized).
  const finished = timeline.status !== "running" && timeline.status !== "idle";
  const report = useMemo(
    () =>
      finished && lastRun
        ? analyze({
            events: timeline.events,
            layers: timeline.layers,
            context: lastRun.context,
            registry: timeline.canaries,
            globalDiff: timeline.globalDiff,
            ...(timeline.stats && { stats: timeline.stats }),
            limitsHit: timeline.limitsHit,
            code: lastRun.code,
          })
        : null,
    [
      finished,
      lastRun,
      timeline.events,
      timeline.layers,
      timeline.canaries,
      timeline.globalDiff,
      timeline.stats,
      timeline.limitsHit,
    ],
  );
  // The timeline shows severities as the analysis escalated them.
  const shownTimeline = useMemo(
    () =>
      report?.escalations.length
        ? { ...timeline, events: [...applyEscalations(timeline.events, report.escalations)] }
        : timeline,
    [timeline, report],
  );

  /** Click-to-source: switch to the site's layer tab and flash its line. */
  const revealSite = useCallback(
    (site: SourceSite) => {
      setSelectedTab(resolveTab(site.layer, timeline.layers));
      setReveal({ layer: site.layer, line: site.line });
    },
    [timeline.layers],
  );

  /** A Report evidence link: show the event's timeline row and its source line. */
  const showEvidence = useCallback(
    (seq: number) => {
      setBehaviorTab("timeline");
      setHighlight({ seq });
      const site = timeline.events.find((e) => e.seq === seq)?.site;
      if (site) revealSite(site);
    },
    [timeline.events, revealSite],
  );

  // ⌘/Ctrl+Enter also works outside the editor (ADR-0020).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.defaultPrevented) {
        event.preventDefault();
        start();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [start]);

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center gap-3 border-b border-border bg-bg-dark px-4 py-2">
        <span className="font-semibold">◐ Afterimage</span>
        <span className="hidden text-fg-dark md:inline">
          Shadow-mode permission discovery for untrusted JavaScript
        </span>
        <ExamplePicker
          examples={PICKER_EXAMPLES}
          selected={example.id}
          onSelect={(next) => {
            code.current = next.code;
            setExample(next);
            setReveal(null);
            setSettings(settingsForExample(next.context));
          }}
        />
        <button
          type="button"
          data-testid="context-button"
          aria-expanded={contextOpen}
          onClick={() => setContextOpen((open) => !open)}
          className="flex items-center gap-1.5 rounded border border-border px-2 py-1 text-fg-dark hover:text-fg focus-visible:outline-2 focus-visible:outline-blue"
        >
          <SlidersHorizontal size={14} aria-hidden /> Context
          {isModified(settings) && (
            <span
              data-testid="context-modified"
              className="rounded bg-orange/20 px-1 text-[10px] font-semibold uppercase text-orange"
            >
              modified
            </span>
          )}
        </button>
        <div className="ml-auto flex items-center gap-3">
          <span
            data-testid="engine-status"
            className="hidden font-mono text-xs text-comment lg:inline"
          >
            {describeEngine(engine)}
          </span>
          <RunButton running={running} disabled={!ready} onRun={start} onStop={stop} />
          <ThemeToggle theme={theme} onToggle={toggle} />
        </div>
      </header>
      <main className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <section className="flex min-h-[40vh] min-w-0 flex-1 flex-col border-border lg:min-h-0 lg:basis-[55%] lg:border-r">
          <p
            data-testid="explainer"
            className="border-b border-border bg-bg-dark px-3 py-1 text-xs text-fg-dark"
          >
            <span className="font-semibold">What to look for:</span> {example.explainer}
          </p>
          <div className="min-h-0 min-w-0 flex-1">
            <EditorPanel
              docKey={example.id}
              initialCode={example.code}
              theme={theme}
              layers={timeline.layers}
              tab={tab}
              onTab={(next) => {
                setSelectedTab(next);
                setReveal(null);
              }}
              reveal={reveal}
              hover={hover}
              onChange={(value) => {
                code.current = value;
              }}
              onRun={start}
            />
          </div>
        </section>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:basis-[45%]">
          <Pane
            label="Output"
            testId="output"
            tabs={[
              { id: "console", label: "Console", content: <ConsolePane timeline={timeline} /> },
              { id: "page", label: "Page preview", content: <PagePreview timeline={timeline} /> },
            ]}
          />
          <div className="border-t border-border" />
          <Pane
            label="Behavior"
            testId="behavior"
            active={behaviorTab}
            onActive={setBehaviorTab}
            tabs={[
              {
                id: "timeline",
                label: "Timeline",
                content: (
                  <TimelinePane
                    timeline={shownTimeline}
                    onReveal={revealSite}
                    onHover={setHover}
                    highlight={highlight}
                  />
                ),
              },
              {
                id: "report",
                label: "Report",
                content: (
                  <ReportPane
                    report={report}
                    status={timeline.status}
                    events={shownTimeline.events}
                    onEvidence={showEvidence}
                    onOpenLayer={(layer) => {
                      setSelectedTab(resolveTab(layer, timeline.layers));
                      setReveal(null);
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      </main>
      {contextOpen && (
        <ContextDrawer
          settings={settings}
          onChange={setSettings}
          onClose={() => setContextOpen(false)}
        />
      )}
    </div>
  );
}
